/**
 *
 *  Rewards page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 26/03/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Title, Meta } from "react-meta-elements";
import Modal from "react-modal";
import Slider from "react-slick";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import SliderPrevArrow from "../SliderPrevButton/SliderPrevButton";
import SliderNextArrow from "../SliderNextButton/SliderNextButton";
import TitleWithSeparator from "../TitleWithSeparator/TitleWithSeparator";
import AchievementCard from "./AchievementCard/AchievementCard";
import ReferralModal from "../ReferralModal/ReferralModal";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";

// Icons
import CheckNiceIcon from "../../icons/CheckboxOIcon";
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import GiftBigIcon from "../../icons/GiftBigIcon";
import CopyIcon from "../../icons/CopyIcon";
import CloseIcon from "../../icons/CloseIcon";

// Images
import referAFriendImg from "./refer-a-friend.jpg";
import referAFriendImg2x from "./refer-a-friend@2x.jpg";
import Rewards from "./Rewards";

export default function RewardsPage() {
  const { OfferId } = useParams();

  const { theme } = useTheme();
  const { region } = useRegion();

  const {
    user,
    referral,
    customerMembership,
    loadCustomerMembership,
    getReferral,
    clearCustomerMembership,
    customerMembershipLoaded,
  } = useAppState();
  const [infoModalTitle, setInfoModalTitle] = useState("Momentum");
  const [infoModalLogo, setInfoModalLogo] = useState();
  const [infoModalMsg, setInfoModalMsg] = useState(
    `You’re building consistency—every step forward fuels your journey.`
  );
  const [infoModalReceived, setInfoModalReceived] = useState(false);
  const [infoModalPoints, setInfoModalPoints] = useState(100);
  const [infoModalCondition, setInfoModalCondition] =
    useState("After 3 Classes");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

  const heroRef = React.useRef(null);
  const sliderRef = React.useRef(null);
  const infoModalResolveRef = useRef(null);

  const isUserLoggedIn = !!(user && user.id);

  const slickInitialised = true;

  useEffect(
    () => {
      if (!user) {
        return;
      }

      getReferral(user.id);
    },
    [user] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const openInfoModal = useCallback((msg = infoModalMsg) => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);

      setInfoModalTitle("Verification Email has been Sent");
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const handleShareClick = useCallback(() => {
    setIsReferralModalOpen(true);
  }, []);

  const closeReferralModal = useCallback(() => {
    setIsReferralModalOpen(false);
  }, []);

  const handleAchievementCardClick = useCallback((reward) => {
    openInfoModal(reward.description);
    setInfoModalLogo(reward.logo);
    setInfoModalTitle(reward.title);
    setInfoModalPoints(reward.points);
    setInfoModalCondition(reward.condition);
    setInfoModalReceived(reward.received);
  }, []);

  var formattedDescription = "";

  const rewardBalance = ((referral ? referral.amount : 0) / 100).toFixed(0);

  return (
    <>
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content={`On-demand, in person, personal training in Los Angeles`}
      />
      <Header behaviour="none" isLoggedIn={isUserLoggedIn} heroRef={heroRef} />

      <div />

      <div
        ref={heroRef}
        className="lg:tbk-min-h-[500px]_ tbk-relative tbk-flex tbk-h-42 tbk-flex-col tbk-overflow-hidden tbk-bg-rewards-hero-gradient tbk-pt-0 lg:tbk-h-60"
      >
        <div className="lg:tbk--bottom-2_ tbk-absolute tbk-right-6 tbk-top-1 tbk-h-34 lg:tbk-bottom-0 lg:tbk-right-42 lg:tbk-top-2 lg:tbk-h-60">
          <Rewards className="" />
        </div>

        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="lg:tbk-mt-7_ lg:tbk-mt-2_ tbk-mb-7 tbk-flex tbk-h-full tbk-grow tbk-flex-col tbk-justify-center lg:tbk-items-center">
            <div className="tbk-mb-0.5 tbk-flex tbk-flex-col md:tbk-justify-start lg:tbk-items-center lg:tbk-justify-center">
              <div className="tbk-mb-3 tbk-mt-2 tbk-flex tbk-grow lg:tbk-mb-4">
                <div className="tbk-mx-3 tbk-w-full sm:tbk-mx-auto">
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle tbk-text-basic-white lg:tbk-text-center">
                    Welcome to
                  </div>
                  <div className="tbk-text-title-bold lg:tbk-text-epic-bold tbk-text-basic-white lg:tbk-text-center">
                    Rewards
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex">
        <div className="tbk-container tbk-mx-auto">
          <div className="tbk-mx-2 tbk--mt-5 tbk-mb-4 tbk-min-w-0 sm:tbk-mx-auto lg:tbk-mb-6 lg:tbk-w-132">
            <div className="tbk-text-center_ tbk-relative tbk-rounded-2xl tbk-bg-basic-white tbk-py-1.5 tbk-shadow-trube">
              <div className="tbk-text-center_ tbk-text-h3-subtitle tbk-flex tbk-items-center tbk-p-2 tbk-text-primary">
                <GiftBigIcon className="tbk-mr-2" size={32} />
                Balance:
                <span className="tbk-ml-1 tbk-text-green">
                  {rewardBalance} points
                </span>
              </div>
            </div>
          </div>

          <div className="tbk-flex tbk-justify-center tbk-px-2 tbk-pb-4 lg:tbk-px-0 lg:tbk-pb-0">
            <div className="lg:tbk-mb-0 lg:tbk-w-156">
              <h2 className="tbk-text-title-bold lg:tbk-text-h2-black tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-4 lg:tbk-text-center lg:!tbk-leading-tight">
                Sweat, Earn, Redeem!
              </h2>
              <p className="tbk-text-main tbk-mb-7_ tbk-text-primary lg:tbk-px-2 lg:tbk-text-center">
                Unlock achievements, earn points, and turn your wellness into
                rewards! Every milestone gets you closer to free classes or
                membership perks.
                <br className="tbk-hidden lg:tbk-block" />
                <br />1 point ={" "}
                {(user ? user.region : region) === "US" ? "$" : "£"}1. Use them
                to book your next private session, class or more! Start earning
                today.
              </p>
            </div>
          </div>
        </div>
      </div>

      {referral?.achievement?.length ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-overflow-hidden tbk-pb-7 tbk-pt-4 lg:tbk-pb-7 lg:tbk-pt-7">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8">
            <TitleWithSeparator
              className="tbk-mb-3"
              titleClass="tbk-text-center tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
              separatorClass="tbk-border-blue-grey-light"
            >
              Achievements
            </TitleWithSeparator>

            <div className="tb-slider tbk-flex-nowrap tbk-py-2 xl:tbk-justify-center">
              {referral.achievement.map((reward, index) => (
                <div
                  className={`tb-slide tbk-pl-1 tbk-pr-1 lg:tbk-min-w-0 lg:tbk-shrink`}
                  key={index}
                >
                  <div className="content slide-bg tbk-h-full tbk-w-full">
                    <div
                      // to={`/offers/${reword.id}`}
                      // target="_blank"
                      onClick={() => handleAchievementCardClick(reward)}
                    >
                      <AchievementCard
                        logoUrl={reward.logo}
                        title={reward.title}
                        condition={reward.condition}
                        description={reward.description}
                        points={reward.points}
                        pointsTitle={reward.pointsTitle}
                        received={reward.received}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 lg:tbk-pb-7 lg:tbk-pt-7">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="tbk-gap-4_ lg:tbk-gap-8_ tbk-grid tbk-rounded-2xl tbk-shadow-trube lg:tbk-grid-cols-2">
            <div className="tbk-overflow-hidden lg:tbk-h-auto">
              <img
                className="tbk-h-full tbk-w-full tbk-rounded-t-2xl tbk-object-cover lg:tbk-rounded-l-2xl lg:tbk-rounded-tr-none"
                src={referAFriendImg}
                srcSet={`${referAFriendImg} 1x, ${referAFriendImg2x} 2x`}
                alt="Refer a Friend"
              />
            </div>

            <div className="tbk-mb-7_ tbk-p-4 lg:tbk-mb-0">
              <h2 className="tbk-text-title-bold lg:tbk-text-title-bold tbk-mb-0.5 tbk-mb-2 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-left tbk-text-primary">
                Refer a Friend
                <br />& Earn Rewards!
              </h2>
              <div className="tbk-text-h3-subtitle lg:tbk-text-h3-subtitle tbk-mb-1 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-grey">
                <span className="tbk-text-green">+10</span> Points for Each
                Friend
              </div>
              <div className="tbk-text-main tbk-mb-1 tbk-text-blue-grey lg:tbk-mb-4">
                Invite your friends to experience the TruBe Lifestyle
                Collective! Share your unique link, and you'll earn 10 points
                when they join as a member or book their first class. More
                friends, more rewards—start sharing today!
              </div>
              <div className="tbk-mt-4 lg:tbk-mb-0 lg:tbk-mt-4">
                <Button
                  className="tbk-w-full"
                  // disabled={!canSend || isFetching}
                  startIcon={<CopyIcon />}
                  endIcon={<ArrowRightIcon />}
                  onClick={handleShareClick}
                >
                  Share with a Friend
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : Object.assign(
                {},
                { ...modalStyles.info },
                {
                  content: {
                    ...modalStyles.info.content,
                    padding: "32px",
                    width: "480px",
                  },
                }
              )
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-text-coral"
          >
            <CloseIcon />
          </button>

          <img
            src={infoModalLogo}
            alt={"Logo"}
            className={`tbk-mx-auto tbk-mb-2 tbk-h-7 tbk-w-7 tbk-shrink-0${
              infoModalReceived ? "" : " tbk-grayscale"
            }`}
          />

          {/*<h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-1 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>*/}

          <h3
            className={`tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-mb-0.5 tbk-mb-0.5 tbk-text-center ${
              infoModalReceived ? "tbk-text-primary" : "tbk-text-blue-grey"
            } tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap`}
          >
            {infoModalTitle}
          </h3>

          <p className="tbk-text-secondary tbk-mb-0.5 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-center tbk-text-blue-grey">
            <span className="tbk-text-green">+{infoModalPoints / 100}</span>{" "}
            Points {infoModalTitle === "Momentum" ? "per Class" : ""}
          </p>

          <p className="tbk-text-main tbk-mb-2 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-center tbk-text-primary">
            {infoModalCondition}
          </p>

          {infoModalReceived ? (
            <div className="tbk-text-secondary tbk-mb-2 tbk-flex tbk-h-3 tbk-items-center tbk-rounded-2xl tbk-bg-green tbk-text-basic-white">
              <CheckNiceIcon
                width={24}
                height={24}
                className="tbk-absolute tbk-text-green"
              />
              <span className="tbk-ml-3 tbk-mr-1">Unlocked</span>
            </div>
          ) : null}

          <p className="tbk-text-main tbk-mb-4_ tbk-max-w-full tbk-text-center tbk-text-blue-grey">
            {infoModalMsg}
          </p>
        </div>
      </Modal>

      <ReferralModal
        isOpen={isReferralModalOpen}
        // modalTitle="Share the link"
        modalText={
          "Invite your friends to experience the TruBe Lifestyle Collective! Share your unique link, and you'll earn 10 points when they join as a member or book their first class. More friends, more rewards—start sharing today!"
        }
        shareTitle="Share TruBe with a Friend"
        shareText={`Join TruBe and receive ${referral && referral.symbol}${
          referral && referral.receiverReward
        } credit to boost your TruBeApp journey. Sharing is Caring!`}
        // shareLabel={`Link to ${partnerFirstName}'s profile:`}
        shareInput={referral && referral.link}
        shareUrl={referral && referral.url}
        copyFeedbackText="Copied!"
        onClose={closeReferralModal}
      />
    </>
  );
}
